import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { type User } from '@supabase/supabase-js';
import { type Currency, type Language, detectLanguage, getBrowserPreferredLanguage, getCurrency, getStripeCheckoutUrl, getTranslations } from '@/lib/i18n';
import { isLocalizedPublicPath } from '@/lib/publicRoutes';
import { getBrowserSupabase } from '@/lib/browserSupabase';

export interface LanguageContextType {
  language: Language;
  currency: Currency;
  t: ReturnType<typeof getTranslations>;
  stripeUrl: string;
  setLanguage: (lang: Language) => void;
  isLoadingLanguage: boolean;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function createLanguageContextValue(
  language: Language,
  overrides: Partial<Pick<LanguageContextType, 'setLanguage' | 'isLoadingLanguage'>> = {},
): LanguageContextType {
  return {
    language,
    currency: getCurrency(language),
    t: getTranslations(language),
    stripeUrl: getStripeCheckoutUrl(language),
    setLanguage: overrides.setLanguage ?? (() => {}),
    isLoadingLanguage: overrides.isLoadingLanguage ?? false,
  };
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [language, setLanguageState] = useState<Language>(() => detectLanguage(location.pathname));
  const [currency, setCurrency] = useState<Currency>(() => getCurrency(detectLanguage(location.pathname)));
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const userRef = useRef<User | null>(null);
  const isPublicLocalizedRoute = isLocalizedPublicPath(location.pathname);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let isMounted = true;
    let unsubscribe = () => {};

    void (async () => {
      const supabase = await getBrowserSupabase();
      const { data: { session } } = await supabase.auth.getSession();

      if (!isMounted) return;
      setUser(session?.user ?? null);

      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
        if (event === 'TOKEN_REFRESHED') return;
        if (event === 'SIGNED_IN' && nextSession?.user?.id && nextSession.user.id === userRef.current?.id) return;
        setUser(nextSession?.user ?? null);
        setProfileLoaded(false);
      });

      unsubscribe = () => subscription.unsubscribe();
    })().catch((error) => {
      console.error('Error initializing language provider:', error);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!isPublicLocalizedRoute) return;
    const routeLang = detectLanguage(location.pathname);
    setLanguageState(routeLang);
    setCurrency(getCurrency(routeLang));
  }, [isPublicLocalizedRoute, location.pathname]);

  useEffect(() => {
    const loadProfileLanguage = async () => {
      if (isPublicLocalizedRoute) return;

      if (!user) {
        const browserLang = detectLanguage(location.pathname);
        setLanguageState(browserLang);
        setCurrency(getCurrency(browserLang));
        setProfileLoaded(false);
        return;
      }

      if (profileLoaded || typeof window === 'undefined') return;

      setIsLoadingLanguage(true);
      try {
        const supabase = await getBrowserSupabase();
        const { data, error } = await supabase
          .from('profiles')
          .select('language')
          .eq('user_id', user.id)
          .maybeSingle();

        const profileLang = !error && data?.language
          ? (data.language as Language)
          : getBrowserPreferredLanguage();

        setLanguageState(profileLang);
        setCurrency(getCurrency(profileLang));
        console.log('[Language] Loaded default:', profileLang);
        setProfileLoaded(true);
      } catch (error) {
        console.error('Error loading profile language:', error);
      } finally {
        setIsLoadingLanguage(false);
      }
    };

    void loadProfileLanguage();
  }, [user, profileLoaded, isPublicLocalizedRoute, location.pathname]);

  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);
    setCurrency(getCurrency(lang));

    if (user && !isPublicLocalizedRoute && typeof window !== 'undefined') {
      try {
        const supabase = await getBrowserSupabase();
        const { error } = await supabase
          .from('profiles')
          .update({ language: lang })
          .eq('user_id', user.id);

        if (error) {
          console.error('Error saving language preference:', error);
        } else {
          console.log('[Language] Saved to profile:', lang);
        }
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  }, [user, isPublicLocalizedRoute]);

  const value = useMemo(
    () => createLanguageContextValue(language, { setLanguage, isLoadingLanguage }),
    [isLoadingLanguage, language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
